import { useCallback, useContext } from "react";
import { useParams } from "react-router";
import { ServiceContext } from "../../contexts/ServiceContext";
import { useGraphqlMutation } from "../../useCases/graphql";
import {
  CustomerSubmitMutation,
  gqlCustomerSubmit,
  CustomerSubmitInput,
  CustomerRequestPresignUploadResponse,
  CustomerRequestPresignUploadInput,
  CustomerRequestPresignUploadMutation,
  gqlCustomerRequestPresignUpload,
} from "../../generated/graphql";

export function useUploadAsset(): (
  asset: File,
  onUploadProgress: (e: ProgressEvent) => void
) => Promise<string> {
  const requestPresignUpload = useRequestPresignUpload();
  const apiClient = useContext(ServiceContext);
  return useCallback(
    async (asset: File, onUploadProgress: (e: ProgressEvent) => void) => {
      const presignUpload = await requestPresignUpload({
        contentType: asset.type,
        contentLength: asset.size,
        isPublic: true,
      });
      return apiClient.uploadAssets(
        asset,
        presignUpload.assetId,
        presignUpload.url,
        presignUpload.method,
        presignUpload.headers,
        onUploadProgress
      );
    },
    [apiClient, requestPresignUpload]
  );
}

export function useCustomerSubmit(): (
  input: CustomerSubmitInput
) => Promise<boolean> {
  const { companySlug } = useParams<{ companySlug: string }>();
  const mutation = useGraphqlMutation<CustomerSubmitMutation>(
    gqlCustomerSubmit
  );
  return useCallback(
    async (input: CustomerSubmitInput) => {
      const res = await mutation({
        variables: {
          slug: companySlug,
          input,
        },
      });
      if (!res) {
        throw new Error();
      }
      return res.CustomerSubmit;
    },
    [companySlug, mutation]
  );
}

export function useRequestPresignUpload(): (
  input: CustomerRequestPresignUploadInput
) => Promise<CustomerRequestPresignUploadResponse> {
  const { companySlug } = useParams<{ companySlug: string }>();
  const mutation = useGraphqlMutation<CustomerRequestPresignUploadMutation>(
    gqlCustomerRequestPresignUpload
  );
  return useCallback(
    async (input: CustomerRequestPresignUploadInput) => {
      const res = await mutation({
        variables: {
          slug: companySlug,
          input,
        },
      });
      if (!res) {
        throw new Error();
      }
      return res.CustomerRequestPresignUpload;
    },
    [companySlug, mutation]
  );
}
